import { Controller } from '@hotwired/stimulus';

// Mirror of app/lib/files/mime_map.rb PAIRS — keep in sync.
// (Manual sync: a future rake task could emit a JSON sidecar consumed
//  at build time if drift becomes a problem.)
const MIME_TO_EXT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

function extensionsFor(mimes) {
  const exts = mimes.flatMap((m) => MIME_TO_EXT[m] || []);
  return [...new Set(exts)];
}

/**
 * Document Upload Controller (form-level)
 *
 * Form-scope concerns only: conditional field visibility, reactive type
 * narrowing on the file input + descendant file-dropzone, submit progress
 * animation, and file-readiness aggregation across descendant
 * `file-dropzone` controllers.
 *
 * Per-instance dropzone behavior (placeholder/preview/error states, drag
 * and drop, validation) lives in `file_dropzone_controller.js`. Multiple
 * dropzones inside a single form are now supported because each one owns
 * its own controller instance and DOM scope.
 *
 * Backwards-compat: still dispatches `document-upload:ready` when a file is
 * selected so listeners (e.g. `auto-submit` on the MOE invoice form) keep
 * working without changes.
 */
export default class extends Controller {
  static targets = ['customLabelField', 'contractField', 'dateField', 'progress', 'progressBar'];
  static values = {
    contractScopedTypes: { type: Array, default: ['trc'] },
    typesWithExpiry: {
      type: Array,
      default: ['kbis', 'rcp', 'decennale', 'trc', 'urssaf', 'fiscale'],
    },
    formatMap: { type: Object, default: {} }, // { kbis: ['application/pdf', ...], ... }
  };

  connect() {
    this._readyDropzones = new Set();
    this._boundOnSelected = this._onFileSelected.bind(this);
    this._boundOnCleared = this._onFileCleared.bind(this);
    this.element.addEventListener('file-dropzone:selected', this._boundOnSelected);
    this.element.addEventListener('file-dropzone:cleared', this._boundOnCleared);

    // Re-apply narrowing if the form was re-rendered with a type already
    // selected (Turbo stream error path). The change event doesn't fire
    // on connect, so we sync once explicitly.
    this._applyTypeNarrowing(this._currentTypeValue());
  }

  disconnect() {
    this.element.removeEventListener('file-dropzone:selected', this._boundOnSelected);
    this.element.removeEventListener('file-dropzone:cleared', this._boundOnCleared);
    if (this.progressInterval) clearInterval(this.progressInterval);
  }

  // -- Document type toggling --

  typeChanged(event) {
    const type = event.target.value;

    if (this.hasCustomLabelFieldTarget) {
      this.customLabelFieldTarget.classList.toggle('hidden', type !== 'other');
    }

    if (this.hasContractFieldTarget) {
      const isContractScoped = this.contractScopedTypesValue.includes(type);
      this.contractFieldTarget.classList.toggle('hidden', !isContractScoped);
    }

    if (this.hasDateFieldTarget) {
      this.dateFieldTarget.classList.toggle('hidden', !this.typesWithExpiryValue.includes(type));
    }

    this._applyTypeNarrowing(type);
  }

  // -- File-readiness aggregation across descendant file-dropzones --

  _onFileSelected(event) {
    this._readyDropzones.add(event.target);
    // Backwards-compat: re-emit as `document-upload:ready` so listeners
    // like the MOE invoice form's auto-submit keep working.
    this.dispatch('ready', { detail: event.detail, bubbles: true });
  }

  _onFileCleared(event) {
    this._readyDropzones.delete(event.target);
  }

  // -- Upload progress --

  submit(_event) {
    if (this._readyDropzones.size === 0) return;
    if (!this.hasProgressTarget) return;

    this.progressTarget.classList.remove('hidden');
    this.progressTarget.setAttribute('aria-hidden', 'false');
    this._animateProgress();
  }

  // -- Reactive type narrowing --
  //
  // Tightens the file input's `accept` attribute AND the descendant
  // file-dropzone's `allowed-types-value` to the selected document type.
  // Empty map (legacy callers) → no narrowing. 'other' is included in
  // the format map (Documents::TypeRegistry.format_map_for adds it
  // mapped to the category union), so the dropzone stays category-broadest
  // when "Autre document" is picked — that is the correct UX.

  _applyTypeNarrowing(type) {
    if (!type) return;
    const mimes = this.formatMapValue[type];
    if (!mimes || mimes.length === 0) return;

    const fileInput = this.element.querySelector('input[type="file"]');
    if (!fileInput) return; // dropzone not yet in DOM (rare race)

    fileInput.setAttribute('accept', extensionsFor(mimes).join(','));

    // Sync the per-instance file-dropzone validator so it rejects files
    // outside the narrowed MIME list. Stimulus values reactively pick up
    // data-attribute changes.
    const dropzoneEl = fileInput.closest('[data-controller~="file-dropzone"]');
    if (dropzoneEl) {
      dropzoneEl.setAttribute('data-file-dropzone-allowed-types-value', JSON.stringify(mimes));
    }
  }

  _currentTypeValue() {
    const select = this.element.querySelector('select[name="document[document_type]"]');
    return select ? select.value : null;
  }

  _animateProgress() {
    if (!this.hasProgressBarTarget) return;

    let width = 0;
    this.progressInterval = setInterval(() => {
      if (width < 70) {
        width += 3;
      } else if (width < 90) {
        width += 0.5;
      } else {
        clearInterval(this.progressInterval);
        return;
      }
      this.progressBarTarget.style.width = `${width}%`;
      this.progressBarTarget.setAttribute('aria-valuenow', Math.round(width));
    }, 100);
  }
}
